import { Injectable } from '@angular/core';
import { DecedentValidationResponse } from '@interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { DecedentSecurityService } from '@shared/services/decedent-security/decedent-security.service';
import { StopPendingEffectsService } from '@src/app/core/services/stop-pending-effects.service';
import * as dayjs from 'dayjs';
import { catchError, switchMap, takeUntil, withLatestFrom } from 'rxjs';

import {
  DecedentAccountsFailure,
  DecedentAccountsSuccess,
  DecedentValidationFailure,
  DecedentValidationStart,
  DecedentValidationSuccess,
} from '../../actions/decedent-security/decedent-security.actions';
import * as routerActions from '../../actions/router/router.actions';
import * as fromRootStore from '../../index';
import { selectDecedentInfoState } from '../../selectors/decedent-info/decedent-info.selectors';

@Injectable()
export class DecedentSecurityEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private decedentSecurityService: DecedentSecurityService,
    private loggerService: LoggerService,
    private stopPendingEffectsService: StopPendingEffectsService,
  ) {}

  decedentValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DecedentValidationStart),
      withLatestFrom(this.store$.pipe(select(selectDecedentInfoState))),
      switchMap(([, decedentInfo]) => {
        const validationPayload = {
          decedentPoid: decedentInfo.decedentPoid,
          zipCode: decedentInfo.zipCode,
          addressLine1: decedentInfo.addressLine1 || '',
          city: decedentInfo.city || '',
          stateCode: decedentInfo.state || '',
          dateOfBirth: dayjs(decedentInfo.dateOfBirth).format('YYYY-MM-DD'),
        };

        return this.decedentSecurityService.validateDecedent(validationPayload).pipe(
          switchMap((response: DecedentValidationResponse) => {
            if (response.valid) {
              const ssn = decedentInfo.decedentId_private.replace(/-/g, '');
              return this.decedentSecurityService.getDecedentAccounts({ ssn }).pipe(
                switchMap((accounts) => [
                  DecedentValidationSuccess({ payload: { valid: true } }),
                  DecedentAccountsSuccess({ payload: { accounts } }),
                  routerActions.Next(),
                ]),
                catchError(() => {
                  this.loggerService.info(
                    { message: `Decedent accounts call failed, decedent is valid` },
                    LoggerCodes.NULL_FAILURE,
                  );
                  return [
                    DecedentValidationSuccess({ payload: { valid: true } }),
                    DecedentAccountsFailure(),
                    routerActions.Next(),
                  ];
                }),
              );
            } else {
              this.loggerService.info(
                {
                  message: `Decedent is not valid, MW returns valid as false`,
                },
                LoggerCodes.NULL_FAILURE,
              );
              return [
                DecedentValidationSuccess({ payload: { valid: false } }),
                routerActions.Next(),
              ];
            }
          }),
          catchError((error) => {
            this.loggerService.info(
              { message: `Decedent validation call failed, decedent is not valid` },
              LoggerCodes.NULL_FAILURE,
            );
            return [DecedentValidationFailure(), routerActions.Next()];
          }),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );
}
